import { formatDate } from '@frontend/mwm-ui'
import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IPurchase } from '~/types/app-purchase'
import type { IAppUser, USER_TYPE } from '~/types/app-user'
import type { DeviceType } from '~/types/device-type'

import Button from '~/components/Buttons/Button'
import DateInput from '~/components/Inputs/Basic/DateInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { TODAY } from '~/const'
import { useUpdateAppsUserPurchaseMutation } from '~/queries/app-purchases'
import { keys as appsUsersKeys } from '~/queries/app-users'

import '../styles.scss'

interface Values {
  appKey?: string
  deviceType?: DeviceType
  expirationDate: string
  purchasedAt?: string
  sku?: string
  type?: string
}

const validationSchema = Yup.object().shape({
  expirationDate: Yup.string().required(),
})

interface ModalFormProps {
  env: USER_TYPE
  onClose: VoidFunction
  onFailure: (msg: string) => void
  onSuccess: (msg: string) => void
  purchase: IPurchase
  user: IAppUser
}

const UpdatePurchaseForm = ({ env, onClose, onFailure, onSuccess, purchase, user }: ModalFormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const defaultValues = {
    appKey: purchase.app_key || '',
    deviceType: purchase.device_type || '',
    expirationDate: formatDate(purchase.expiration_date, 'YYYY-MM-DD'),
    purchasedAt: formatDate(purchase.purchased_at, 'YYYY-MM-DD'),
    sku: purchase.sku || '',
    type: purchase.type || '',
  }

  /** Queries & Mutations */

  const { control, handleSubmit, register, setValue, trigger } = useForm<Values>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const { mutateAsync: updateAppsUserPurchase } = useUpdateAppsUserPurchaseMutation()

  /** Handlers */

  const handleFormSubmit = async (values: Values) => {
    const { expirationDate } = values

    if (user && purchase.purchase_id) {
      try {
        await updateAppsUserPurchase({
          env,
          expirationDate: expirationDate,
          purchaseId: purchase.purchase_id,
          userId: user.user_id,
        })
        queryClient.invalidateQueries({ queryKey: appsUsersKeys.details(env, user.user_id) })
        onSuccess('Purchase successfully updated')
        onClose()
      } catch (e) {
        onFailure('Purchase could not be updated!')
      }
    }
  }

  const handleDateChange = (date?: string) => {
    date && setValue('expirationDate', date, { shouldDirty: true, shouldTouch: true })
    trigger()
  }

  return (
    <>
      <form
        className="edit-apps-user-form modal-form"
        id="edit-apps-user-form"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <TextInput
          disabled
          label="Application"
          placeholder="Enter an Application"
          type="text"
          {...register('appKey')}
        />
        <TextInput
          disabled
          label="Device Type"
          placeholder="Enter a Device type"
          type="text"
          {...register('deviceType')}
        />
        <TextInput disabled label="Type" placeholder="Enter a Type" type="text" {...register('type')} />
        <TextInput disabled label="Sku" placeholder="Enter a Sku" type="text" {...register('sku')} />
        <TextInput
          disabled
          label="Purchased at"
          placeholder="Enter Purchased date"
          type="text"
          {...register('purchasedAt')}
        />
        <Controller
          control={control}
          name="expirationDate"
          render={() => (
            <DateInput
              dateValue={defaultValues.expirationDate}
              label="Expiration date"
              minDate={TODAY}
              name="expirationDate"
              onDateChange={handleDateChange}
            />
          )}
        />
      </form>
      <ModalFooter>
        <Button ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button form="edit-apps-user-form" type="submit">
          Save
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </>
  )
}

interface ModalProps {
  env: USER_TYPE
  isOpen: boolean
  onClose: VoidFunction
  onFailure: (msg: string) => void
  onSuccess: (msg: string) => void
  purchase?: IPurchase
  user?: IAppUser
}

const UpdatePurchaseModal = ({
  env,
  isOpen,
  onClose,
  onFailure,
  onSuccess,
  purchase,
  user,
}: ModalProps): ReactElement => {
  return (
    <Modal className="edit-apps-user-modal apps-users-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Edit user</ModalHeader>
      {purchase && user ? (
        <UpdatePurchaseForm
          env={env}
          onClose={onClose}
          onFailure={onFailure}
          onSuccess={onSuccess}
          purchase={purchase}
          user={user}
        />
      ) : null}
    </Modal>
  )
}

export default UpdatePurchaseModal
